import React, { useContext, useState, useEffect } from 'react';
import AuthContext from "../../../contexts/auth";
import { useForm } from "react-hook-form";
import { getUser, formatCurrency, sqlDateFormat, getStatusRequests } from "../../../utils/Common";
import { BiSolidBank, BiReceipt } from "react-icons/bi";
import { FaMoneyCheckAlt, FaHistory, FaFileInvoiceDollar,FaSyncAlt } from "react-icons/fa";
import Spinner from "../../../components/Spinner";
import './index.css';
import { useRequests } from '../../../hooks/useRequests';
import { useClient } from '../../../hooks/useClient';
import NotifyC from '../../../components/Notifications';
import DataTable from '../../../components/ui/DataTable';
import Swal from "sweetalert2";

// Componentes separados para mejor organización
const SaldoDisponible = ({ saldoRecaudador, isRotating }) => (
  <div className="d-flex align-items-center">
    <div
      className="text-center"
      style={{
        fontSize: "1rem",
        background: "#dbf5f3",
        padding: "8px 12px",
        borderRadius: "12px",
      }}
    > 
      <div style={{ color: "#0eab9b" }}>Saldo de Recaudador</div>
      {saldoRecaudador === null || isRotating  ? (
        <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 5, marginBottom: 5 }}>
          <Spinner type={0} size={10} color={"#0eab9b"} />
          <Spinner type={0} size={10} color={"#0eab9b"} />
          <Spinner type={0} size={10} color={"#0eab9b"} />
        </div>
      ) : (
        <div className="text-bold" style={{ color: "#0eab9b" }}>
          {formatCurrency(saldoRecaudador)}
        </div>
      )}
    </div>
  </div>
);

const EstadoBadge = ({ estado }) => {
const statusValue = estado; 
  let backgroundColor = "";
  switch (statusValue) {
      case 1:
          backgroundColor = "#FFD700"; 
          break;
      case 2: 
          backgroundColor = "#28a745"; 
          break;
      case 3: 
          backgroundColor = "#dc3545"; 
          break;
      case 4: 
          backgroundColor = "#6c757d"; 
          break;
      default:
          backgroundColor = "#f8f9fa"; 
  }

  return (
      <span
          style={{
              backgroundColor,
              color: "white", // Color del texto
              padding: "5px 10px",
              borderRadius: "5px",
              display: "inline-block",
              textAlign: "center",
              minWidth: "80px",
              fontWeight: "bold",
              fontSize: "15px",
          }}
      >
          {getStatusRequests(statusValue)} {/* Muestra el nombre del estado */}
      </span>
  );
};

const DepositosTable = ({ depositos, isLoading }) => {
    const columns = [
        { Header: "Nro. Depósito", accessor: "IdSolicitud", Cell: ({ cell }) => <span>#{cell.value}</span> },
        { Header: "Fecha de Registro", accessor: "FechaCreacion", Cell: ({value}) => <span>{sqlDateFormat(value, false, true)}</span> },
        { Header: "Monto Depositado", accessor: "SaldoIngresado", Cell: ({ cell }) => formatCurrency(cell.value) },
        { Header: "Estado", accessor: "status", Cell: ({ cell }) => <EstadoBadge estado={cell.value} /> },
    ];
    
    return (
        <div className="table-responsive">
            {isLoading ? (
                <div className="d-flex justify-content-center align-items-center" style={{ height: "200px" }}>
                    <Spinner type={1} size={30} />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={depositos}
                />
            )}
        </div>
    );
};

const Depositos = () => {
    const user = getUser();
    const { getClientById } = useClient();
    const { createRequest, getAllRequests } = useRequests();
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const [saldoRecaudador, setSaldoRecaudador] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isRotating, setIsRotating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [allRequests, setAllRequests] = useState([]);

    const handleReloadClick = async () => {
        setIsRotating(true);
      
        setTimeout(() => {
            handleGetAllRequests();
            getSaldoRecaudador();
            NotifyC.success("Solicitudes actualizadas");
            setIsRotating(false);
     
        }
        , 1000);

    };

    const handleErrorMessages = (error) => {
        const errorMessages = {
            "ACTION_TYPE_INVALID": "Tipo de acción inválido.",
            "CLIENT_NOT_FOUND_IN_SYSTEM": "Usuario no encontrado en el sistema.",
            "OFFICE_NOT_FOUND": "Estafeta no encontrada.",
            "OFFICE_NOT_ACTIVE": "La estafeta se encuentra desactivada. Por favor, contacta al administrador para activar la estafeta.",
            "ERROR_CREATING_REQUEST": "Error al crear la solicitud.",
            "CASHBOX_HAS_PENDING_REQUEST": "La caja tiene una solicitud pendiente.",
            "CASHBOX_NOT_ACTIVE": "La caja se encuentra desactivada. Por favor, contacta al administrador para activar la caja.",
            "INSUFFICIENT_BALANCE_FOR_DEPOSIT": "El saldo disponible es insuficiente para realizar el depósito.",
          };
    
        return errorMessages[error] || "Error al realizar la acción, intentelo de nuevo.";
    };

    const onSubmit = async (data) => {

        setIsSubmitting(true);
        const dataToSend = {
         IdTipoSolicitud: 3,
         IdEstatus: 1, 
         Accion: 6,
         SaldoIngresado: Number(data.monto),
         Comentarios: data.comentarios || null,
         SaldoCliente: saldoRecaudador,
        }
        try {
            const abortController = new AbortController();
            const response = await createRequest(abortController.signal, dataToSend);
            if (response.code === 200) {
             handleGetAllRequests();
             Swal.fire({
                    title:'¡Solicitud Registrada!',
                    text: 'Solicitud de depósito registrado exitosamente.',
                    icon: "success",
                    confirmButtonText: "Ok",
                    customClass: {
                      confirmButton: 'custom-confirm-button',
                    }
                });
             reset();
            } else {
                NotifyC.error(handleErrorMessages(response.code), { duration: 6000 });
            }
        } catch (error) {
            console.error("Error al registrar depósito:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGetAllRequests = async () => {
        const abortController = new AbortController();
        const response = await getAllRequests(abortController.signal);
        if (response?.code === 200) {
            const filtered = Array.isArray(response.payload)
                ? response.payload.filter(item => item.IdTipoSolicitud === 3)
                : [];
            setAllRequests(filtered);
        } else {
            NotifyC.error("No se encontraron solicitudes.", { duration: 6000 });
            setAllRequests([]);
        }
    };

    const ultimoPendiente = allRequests.length > 0 && allRequests[0].status === 1;

    const getSaldoRecaudador = async () => {
        const abortController = new AbortController();
        const response = await getClientById(abortController.signal, { IdCliente: user.IdCliente });
        if (response?.length > 0) {
            setSaldoRecaudador(response[0]?.Saldo);
        } else {
            setSaldoRecaudador(0);
            NotifyC.error("No se pudo obtener el saldo del recaudador");
        }
    };
   
    useEffect(() => {
        handleGetAllRequests();
        getSaldoRecaudador();
    }, []);

    useEffect(() => {
          setIsLoading(true);
          setTimeout(() => {
              setIsLoading(false);
          }, 1000);
      }, []);

    return (
      <>
        {/* Encabezado */}
        <div className="dashboardCard mb-4">
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
            <div className="d-flex align-items-center mb-3 mb-md-0" style={{ color:'black', gap: '15px' }}>
              <BiSolidBank size={28} />
              <h5 className="mb-0">Registrar Depósitos</h5>
            </div>
            <SaldoDisponible saldoRecaudador={saldoRecaudador} isRotating={isRotating} />
          </div>
        </div>

        {/* Formulario de depósito */}
        <div className="dashboardCard deposit mb-4">
          <div className="card-header bg-light">
            <h5 className="mb-0">Nuevo Depósito</h5>
          </div>
          <div className="card-body">  
            {ultimoPendiente && (
            <div className="alert alert-warning mb-3">
              Tienes un depósito pendiente. Debes esperar a que sea procesado antes de registrar uno nuevo.
            </div>
          )}    
          <form onSubmit={handleSubmit(onSubmit)}>
              {/* Contenedor de los inputs */}
              <div className="d-flex flex-column gap-3 mb-4">
                <div className="row g-3">
                  {/* Input Monto */}
                  <div className="col-md-6">
                    <div className="d-flex align-items-stretch">
                      <div className="box-input-primary d-flex align-items-center px-3"
                      style={{ color: 'black' }}
                      >Monto</div>
                      <input
                        type="number"
                        step="any"	
                        name='monto'
                        className={`form-control flex-grow-1 ${errors.monto ? 'is-invalid' : ''}`}
                         ref={register({
                            required: "Este campo es obligatorio",
                            min: { value: 0.01, message: "El monto debe ser mayor a 0" },
                            validate: value => 
                              parseFloat(value) <= saldoRecaudador 
                                ? true 
                                : "El monto no puede exceder el saldo disponible",
                          })}
                        placeholder="Introducir monto a depositar"
                      />
                      {errors.monto && <div className="invalid-feedback">{errors.monto.message}</div>}
                    </div>
                  </div>
                  
                  {/* Input Comentarios */}
                  <div className="col-md-6">
                    <div className="d-flex align-items-stretch">
                      <div className="box-input-secondary d-flex align-items-center px-3">Comentarios</div>
                      <textarea
                        name='comentarios'
                        className={`form-control flex-grow-1 ${errors.comentarios ? 'is-invalid' : ''}`}
                        ref={register()}
                        placeholder="...."
                        rows={1}
                      />
                      {errors.comentarios && <div className="invalid-feedback">{errors.comentarios.message}</div>}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Botón de submit */}
              <div className="d-flex justify-content-center mt-2">
                <button
                  type="submit"
                  className="btn btn-blue"
                  style={{ width: '100%', maxWidth: '100px' }}
                  disabled={isLoading || ultimoPendiente || isSubmitting}
                >
                  {isSubmitting ? <Spinner type={1} size={15} /> : 'Depositar'}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Historial de depósitos */}
        <div className="dashboardCard" style={{ color: 'black' }}>
           <div className="d-flex align-content-center justify-content-between">
             <h5 className="mb-3">
              <FaHistory className="mr-2" />
              Historial de Depósitos
            </h5>
              <FaSyncAlt
                size={20}
                style={{ cursor: "pointer", animation: isRotating ? "spin 2s linear infinite" : "none" }}
                onClick={() => handleReloadClick()}
            />           
          </div>
          <div className="card-body p-0">
            <DepositosTable depositos={allRequests} isLoading={isRotating ? isRotating : isLoading} />
          </div>
        </div>
    
    </>);
};

export default Depositos;